// EmailSettingsPage — раздел «Почта» (/settings/email; V15 «Почта» W7, ТЗ §3.8).
// Один маршрут с вкладками «Аккаунты» / «Шаблоны» / «История» (решено ТЗ: меньше маршрутов).
// Аккаунты: GET /api/email/settings — SMTP-карточка (провайдер/адрес/статус/активный) +
// SmtpAccountDialog (маска секрета hasSecret), Gmail-карточка + «Подключить Gmail»
// (GET authorize → window.location.assign(authorizeUrl)) + «Проверить подключение»
// (POST refresh → 502 → Alert «переподключите»), «Активировать» (PUT activate),
// «Удалить SMTP» (DELETE smtp), «Отключить Gmail» (DELETE /api/email/settings/gmail →
// 204 → Alert «Gmail отключён»; 404 — уже отключён → карточка скрывается без ошибки).
// GET settings → 404 = аккаунтов нет → единый пустой state «Почта не настроена»
// (кнопка «Добавить SMTP-аккаунт»; «Подключить Gmail» — только при googleOAuthEnabled === true).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import { emailApi, type EmailAccount } from '@/api/email';
import { extractApiErrorMessage } from '@/lib/apiError';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { t } from '@/i18n';
import { SmtpAccountDialog } from './components/SmtpAccountDialog';
import { EmailDeliveriesHistory } from './components/EmailDeliveriesHistory';
import { EmailTemplatesPage } from '@/pages/email';

/** Индексы вкладок раздела «Почта» (0 — Аккаунты, 1 — Шаблоны, 2 — История; ТЗ §3.8). */

/** Ошибка 404 (Gmail уже отключён — контракт «уже удалён»; канон ReceiptUploadDialog 422). */
function isNotFoundError(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 404;
}

interface EmailAccountCardProps {
  account: EmailAccount;
  /** Активный аккаунт (единственный; для активного кнопка «Активировать» скрыта). */
  isActive: boolean;
  /** Операции карточки (мутации страницы — единая точка invalidate-расписания). */
  actions: {
    onEdit: () => void;
    onActivate: (accountId: string) => void;
    onDelete: () => void;
    onRefresh: () => void;
    onDisconnect: () => void;
    activatePending: boolean;
    deletePending: boolean;
    refreshPending: boolean;
    disconnectPending: boolean;
    refreshError: unknown;
  };
}

/** Карточка аккаунта (SMTP/GMAIL): статус, адрес, действия (Редактировать/Активировать/Удалить/Проверить). */
function EmailAccountCard({ account, isActive, actions }: EmailAccountCardProps): JSX.Element {
  const smtpMeta = account.provider === 'SMTP'
    ? `${account.smtpHost}:${account.smtpPort} • ${account.username} • ${t(account.smtpSecurity === 'STARTTLS' ? 'email.smtp.security.STARTTLS' : 'email.smtp.security.SSL')}`
    : null;

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap" useFlexGap>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {account.provider === 'GMAIL' ? <CheckCircleIcon color="success" fontSize="small" /> : <EmailIcon color="primary" fontSize="small" />}
            <Typography variant="subtitle1">
              {account.provider === 'GMAIL' ? t('email.accounts.gmailConnected') : t('email.accounts.smtpTitle')}
            </Typography>
            {isActive ? <Chip size="small" color="success" label={t('email.accounts.active')} /> : null}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {account.fromEmail}
          </Typography>
          {smtpMeta ? (
            <Typography variant="body2" color="text.secondary">
              {smtpMeta}
            </Typography>
          ) : null}
          {actions.refreshError && account.provider === 'GMAIL' ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {extractApiErrorMessage(actions.refreshError, t('email.accounts.gmailError'))}
            </Alert>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1}>
          {account.provider === 'SMTP' ? (
            <Button size="small" onClick={actions.onEdit}>
              {t('common.edit')}
            </Button>
          ) : null}
          {account.provider === 'GMAIL' ? (
            <Button size="small" variant="outlined" disabled={actions.refreshPending} onClick={actions.onRefresh}>
              {t('email.accounts.checkConnection')}
            </Button>
          ) : null}
          {account.provider === 'GMAIL' ? (
            <Button size="small" color="error" disabled={actions.disconnectPending} onClick={actions.onDisconnect}>
              {t('email.accounts.disconnectGmail')}
            </Button>
          ) : null}
          {!isActive ? (
            <Button
              size="small"
              variant="outlined"
              disabled={actions.activatePending}
              onClick={() => actions.onActivate(account.id)}
            >
              {t('email.accounts.activate')}
            </Button>
          ) : null}
          {account.provider === 'SMTP' ? (
            <Button size="small" color="error" disabled={actions.deletePending} onClick={actions.onDelete}>
              {t('email.accounts.delete')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}

/** Страница «Почта»: вкладки аккаунты/шаблоны/история. */
export function EmailSettingsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [smtpOpen, setSmtpOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<EmailAccount | null>(null);

  const settingsQuery = useQuery({
    queryKey: ['email', 'settings'],
    queryFn: () => emailApi.getSettings(),
    retry: false,
  });

  const activateMutation = useMutation({
    mutationFn: (accountId: string) => emailApi.activate(accountId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email', 'settings'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => emailApi.deleteSmtp(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email', 'settings'] });
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => emailApi.refreshGmail(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email', 'settings'] });
    },
  });

  const deleteGmailMutation = useMutation({
    mutationFn: () => emailApi.deleteGmail(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email', 'settings'] });
    },
    onError: (error) => {
      // 404 — аккаунт уже отключён (например, отозван в Google): «уже удалён» → тихо скрыть карточку.
      if (isNotFoundError(error)) {
        void queryClient.invalidateQueries({ queryKey: ['email', 'settings'] });
      }
    },
  });

  const connectGmail = async (): Promise<void> => {
    const { authorizeUrl } = await emailApi.getAuthorizeUrl();
    // Переход на Google consent (authorizeUrl с state из email_oauth_flows, §3.5).
    window.location.assign(authorizeUrl);
  };

  const accounts = settingsQuery.data?.accounts ?? [];
  const activeAccountId = settingsQuery.data?.activeAccountId ?? null;
  const gmailConnected = accounts.some((a) => a.provider === 'GMAIL');
  /** Кнопка Gmail только при явном true; 404/нет поля → SMTP-only. */
  const googleOAuthEnabled = settingsQuery.data?.googleOAuthEnabled === true;

  /** Единый пустой state «Почта не настроена»: и при 404 GET settings, и при пустом списке аккаунтов. */
  const renderEmptyAccounts = (): JSX.Element => (
    <Stack spacing={2}>
      <EmptyState title={t('email.empty.title')} description={t('email.empty.desc')} />
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingAccount(null); setSmtpOpen(true); }}
        >
          {t('email.empty.addSmtp')}
        </Button>
        {googleOAuthEnabled ? (
          <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => void connectGmail()}>
            {t('email.empty.connectGmail')}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );

  const renderAccounts = (): JSX.Element => {
    if (settingsQuery.isLoading) {
      return <LoadingSpinner label={t('common.loading')} />;
    }
    if (settingsQuery.isError) {
      // 404 = «аккаунтов нет» (не техническая ошибка) → пустой state с кнопками, а не error-Alert.
      if (isNotFoundError(settingsQuery.error)) {
        return renderEmptyAccounts();
      }
      return (
        <Alert severity="error">
          {settingsQuery.error instanceof Error ? settingsQuery.error.message : t('email.accounts.gmailError')}
        </Alert>
      );
    }
    if (accounts.length === 0) {
      return renderEmptyAccounts();
    }

    return (
      <Stack spacing={2}>
        {accounts.map((account) => (
          <EmailAccountCard
            key={account.id}
            account={account}
            isActive={account.id === activeAccountId}
            actions={{
              onEdit: () => { setEditingAccount(account); setSmtpOpen(true); },
              onActivate: (accountId) => activateMutation.mutate(accountId),
              onDelete: () => deleteMutation.mutate(),
              onRefresh: () => refreshMutation.mutate(),
              onDisconnect: () => deleteGmailMutation.mutate(),
              activatePending: activateMutation.isPending,
              deletePending: deleteMutation.isPending,
              refreshPending: refreshMutation.isPending,
              disconnectPending: deleteGmailMutation.isPending,
              refreshError: refreshMutation.error,
            }}
          />
        ))}
        {!gmailConnected && googleOAuthEnabled ? (
          <Box>
            <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => void connectGmail()}>
              {t('email.empty.connectGmail')}
            </Button>
          </Box>
        ) : null}
      </Stack>
    );
  };

  return (
    <Box>
      <PageHeader title={t('email.title')} subtitle={t('email.subtitle')} />
      <Tabs value={tab} onChange={(_, next) => setTab(next)} aria-label={t('email.title')}>
        <Tab label={t('email.tabs.accounts')} />
        <Tab label={t('email.tabs.templates')} />
        <Tab label={t('email.tabs.deliveries')} />
      </Tabs>
      {deleteGmailMutation.isSuccess ? (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => deleteGmailMutation.reset()}>
          {t('email.accounts.gmailDisconnected')}
        </Alert>
      ) : null}
      {deleteGmailMutation.isError && !isNotFoundError(deleteGmailMutation.error) ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {extractApiErrorMessage(deleteGmailMutation.error, t('email.accounts.gmailError'))}
        </Alert>
      ) : null}
      <Box sx={{ mt: 2 }}>
        {tab === 0 ? renderAccounts() : tab === 1 ? <EmailTemplatesPage /> : <EmailDeliveriesHistory />}
      </Box>

      <SmtpAccountDialog open={smtpOpen} onClose={() => setSmtpOpen(false)} account={editingAccount} />
    </Box>
  );
}
